import { Injectable, effect, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';

import { NotificationService } from '@shared/services/notification/notification.service';

/**
 * Pone el título de la pestaña a partir de la clave i18n de la ruta.
 *
 * <p>El `title` de cada ruta es una clave (`titles.*`), no un texto: así la pestaña habla el idioma
 * del usuario y cambia en caliente con él. Una clave inexistente se muestra tal cual, que degrada
 * con elegancia en vez de dejar la pestaña vacía.
 *
 * <p><b>Y lleva el contador de no leídas (M17.3 RF-012).</b> El requisito pide que con la pestaña
 * en segundo plano el título diga «(3) NEXUS PM — Dashboard», y que al volver y leer vuelva a su
 * forma normal. Faltaba por completo: quien trabaja con la aplicación en una pestaña de fondo —que
 * es como se usa— no tenía ninguna señal de que hubiera algo nuevo.
 *
 * <p><b>Por qué aquí y no en un servicio aparte.</b> Porque esta clase es la única dueña de
 * `document.title`: lo reescribe en cada navegación. Un servicio que pusiera el contador por su
 * cuenta lo perdería en el siguiente cambio de pantalla, y al revés — dos escritores del mismo
 * valor acaban pisándose.
 */
@Injectable({ providedIn: 'root' })
export class LocalizedTitleStrategy extends TitleStrategy {
  private readonly title = inject(Title);
  private readonly transloco = inject(TranslocoService);
  private readonly notifications = inject(NotificationService);
  private currentKey: string | null = null;

  /**
   * Si la pestaña está oculta.
   *
   * <p>El contador sólo se pinta en segundo plano, como pide el requisito: con la pestaña delante
   * el usuario ya ve la campana, y duplicar el número en el título sólo añade ruido al cambiar de
   * pantalla.
   */
  private oculta = typeof document !== 'undefined' && document.hidden;

  constructor() {
    super();
    this.transloco.langChanges$.subscribe(() => this.applyTitle());

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        this.oculta = document.hidden;
        this.applyTitle();
      });
    }
    // El contador es una señal: el efecto repinta el título en cuanto llega un aviso por SSE,
    // sin esperar a la siguiente navegación.
    effect(() => {
      this.notifications.unreadCount();
      this.applyTitle();
    });
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.currentKey = this.buildTitle(snapshot) ?? null;
    this.applyTitle();
  }

  private applyTitle(): void {
    if (!this.currentKey) {
      return;
    }
    const translated = this.transloco.translate(this.currentKey);
    // Clave inexistente → Transloco devuelve la propia clave; en ese caso usamos el texto literal.
    const base = translated && translated !== this.currentKey ? translated : this.currentKey;
    this.title.setTitle(this.conContador(base));
  }

  /** El título con el contador delante, si toca. */
  private conContador(base: string): string {
    const sinLeer = this.notifications.unreadCount();
    return this.oculta && sinLeer > 0 ? `(${sinLeer}) ${base}` : base;
  }
}
